/**
 * animal-presets.js
 * Thư viện cấu hình dữ liệu và Bộ tạo Vân phủ động (Procedural Canvas Texture)
 * dành cho các loài động vật Voxel Trái Đất (Earth Animals System).
 * Tối ưu hóa: 2 Khung cơ sở chính (Quadruped & Avian) + Tham số co giãn (Morph Ratios).
 */

import { generateAnimalTexture, shadeColor } from './animals/animal-textures.js';

export { generateAnimalTexture, shadeColor };

/**
 * Danh mục 8 loài động vật cơ bản trên Trái Đất
 */
export const ANIMAL_PRESETS = {
  // ==========================================
  // I. ĐỘNG VẬT 4 CHÂN (QUADRUPED)
  // ==========================================

  // 1. Chó (Dog)
  DOG: {
    id: 'DOG',
    name: 'Chó Voxel (Dog)',
    icon: '🐶',
    archetype: 'quadruped',
    desc: 'Chó nhà trung thành, dáng đi hoạt bát, mõm vươn và đuôi luôn vẫy mừng.',
    defaultVariant: 'shiba',
    variants: {
      shiba: {
        name: 'Chó Vàng (Shiba)',
        baseColor: '#d97706',
        patternType: 'solid_fur',
        bellyColor: '#fef3c7',
        snoutColor: '#fef3c7',
        noseColor: '#0f172a',
        eyeColor: '#0f172a',
        blushColor: '#fca5a5'
      },
      dalmatian: {
        name: 'Chó Đốm (Dalmatian)',
        baseColor: '#f8fafc',
        patternColor: '#0f172a',
        patternType: 'dalmatian_spots',
        bellyColor: '#f8fafc',
        snoutColor: '#f8fafc',
        noseColor: '#0f172a',
        eyeColor: '#1e293b',
        blushColor: '#fca5a5'
      },
      black: {
        name: 'Chó Mực (Black)',
        baseColor: '#1e293b',
        patternType: 'solid_fur',
        bellyColor: '#334155',
        snoutColor: '#1e293b',
        noseColor: '#020617',
        eyeColor: '#f8fafc',
        blushColor: '#fca5a5'
      }
    },
    morph: {
      sizeClass: 'small',
      scale: 0.95,
      body: { length: 0.48, width: 0.34, height: 0.32 },
      head: { sizeX: 0.32, sizeY: 0.3, sizeZ: 0.28 },
      snout: { length: 0.12, width: 0.2, height: 0.13 },
      legs: { height: 0.16, width: 0.09, depth: 0.09, spacingX: 0.24, spacingZ: 0.32 },
      ears: { type: 'pointy', width: 0.07, height: 0.11, depth: 0.05 },
      tail: { type: 'wagging_up', length: 0.2, thickness: 0.06, angle: 0.7 }
    }
  },

  // 2. Mèo (Cat)
  CAT: {
    id: 'CAT',
    name: 'Mèo Voxel (Cat)',
    icon: '🐱',
    archetype: 'quadruped',
    desc: 'Mèo nhanh nhẹn, tai nhọn dựng cao, bước chân êm ái và đuôi uốn lượn.',
    defaultVariant: 'tabby',
    variants: {
      tabby: {
        name: 'Mèo Mướp (Tabby)',
        baseColor: '#64748b',
        patternColor: '#1e293b',
        patternType: 'tabby_stripes',
        bellyColor: '#cbd5e1',
        snoutColor: '#cbd5e1',
        noseColor: '#fb7185',
        eyeColor: '#84cc16',
        blushColor: '#fda4af'
      },
      ginger: {
        name: 'Mèo Vàng (Ginger)',
        baseColor: '#f97316',
        patternColor: '#c2410c',
        patternType: 'tabby_stripes',
        bellyColor: '#ffedd5',
        snoutColor: '#ffedd5',
        noseColor: '#fb7185',
        eyeColor: '#eab308',
        blushColor: '#fda4af'
      },
      calico: {
        name: 'Mèo Tam Thể (Calico)',
        baseColor: '#ffffff',
        patternColor: '#18181b',
        accentColor: '#ea580c',
        patternType: 'calico',
        bellyColor: '#ffffff',
        snoutColor: '#ffffff',
        noseColor: '#fb7185',
        eyeColor: '#06b6d4',
        blushColor: '#fda4af'
      },
      tuxedo: {
        name: 'Mèo Tuxedo',
        baseColor: '#18181b',
        patternType: 'solid_fur',
        bellyColor: '#ffffff',
        snoutColor: '#ffffff',
        noseColor: '#fb7185',
        eyeColor: '#a3e635',
        blushColor: '#fda4af'
      }
    },
    morph: {
      sizeClass: 'small',
      scale: 0.85,
      body: { length: 0.44, width: 0.3, height: 0.28 },
      head: { sizeX: 0.3, sizeY: 0.28, sizeZ: 0.26 },
      snout: { length: 0.08, width: 0.14, height: 0.08 },
      legs: { height: 0.14, width: 0.08, depth: 0.08, spacingX: 0.22, spacingZ: 0.28 },
      ears: { type: 'pointy', width: 0.08, height: 0.09, depth: 0.05 },
      tail: { type: 'cat_L', length: 0.22, thickness: 0.05, angle: 0.0 }
    }
  },

  // 3. Bò (Cow)
  COW: {
    id: 'COW',
    name: 'Bò Sữa (Cow)',
    icon: '🐮',
    archetype: 'quadruped',
    desc: 'Bò sữa thân hình chữ nhật vững chãi, hai sừng cong, mũi to phẳng và yếm cổ.',
    defaultVariant: 'dairy',
    variants: {
      dairy: {
        name: 'Bò Sữa (Holstein)',
        baseColor: '#f8fafc',
        patternColor: '#0f172a',
        patternType: 'cow_spots',
        bellyColor: '#f8fafc',
        snoutColor: '#f472b6',
        noseColor: '#be185d',
        hornColor: '#e2e8f0',
        eyeColor: '#0f172a',
        blushColor: '#fda4af'
      },
      brown: {
        name: 'Bò Vàng (Brown)',
        baseColor: '#b45309',
        patternType: 'solid_fur',
        bellyColor: '#fde68a',
        snoutColor: '#fde68a',
        noseColor: '#78350f',
        hornColor: '#fef08a',
        eyeColor: '#0f172a',
        blushColor: '#fca5a5'
      }
    },
    morph: {
      sizeClass: 'large',
      scale: 2.0,
      body: { length: 0.58, width: 0.42, height: 0.38 },
      head: { sizeX: 0.36, sizeY: 0.34, sizeZ: 0.3 },
      snout: { length: 0.14, width: 0.26, height: 0.16 },
      legs: { height: 0.38, width: 0.11, depth: 0.11, spacingX: 0.3, spacingZ: 0.4 },
      ears: { type: 'side', width: 0.1, height: 0.06, depth: 0.04 },
      horns: { hasHorns: true, length: 0.09, thickness: 0.05 },
      tail: { type: 'hanging_tuft', length: 0.22, thickness: 0.045, angle: -0.15 },
      hasUdder: true
    }
  },

  // 4. Lợn (Pig)
  PIG: {
    id: 'PIG',
    name: 'Lợn Hồng (Pig)',
    icon: '🐷',
    archetype: 'quadruped',
    desc: 'Heo hồng mập mạp, chân ngắn chắc nịch, mũi tròn phẳng và chiếc đuôi xoắn xinh xắn.',
    defaultVariant: 'pink',
    variants: {
      pink: {
        name: 'Heo Hồng (Pink)',
        baseColor: '#fbcfe8',
        patternType: 'pig_skin',
        bellyColor: '#fbcfe8',
        snoutColor: '#f43f5e',
        noseColor: '#9f1239',
        eyeColor: '#0f172a',
        blushColor: '#fda4af'
      },
      muddy: {
        name: 'Heo Lấm Lem (Muddy)',
        baseColor: '#fbcfe8',
        patternColor: '#78350f',
        patternType: 'cow_spots',
        bellyColor: '#78350f',
        snoutColor: '#f43f5e',
        noseColor: '#9f1239',
        eyeColor: '#0f172a',
        blushColor: '#fda4af'
      }
    },
    morph: {
      sizeClass: 'medium',
      scale: 1.45,
      body: { length: 0.5, width: 0.38, height: 0.36 },
      head: { sizeX: 0.34, sizeY: 0.32, sizeZ: 0.26 },
      snout: { length: 0.1, width: 0.22, height: 0.13, hasNostrils: true },
      legs: { height: 0.22, width: 0.1, depth: 0.1, spacingX: 0.26, spacingZ: 0.34 },
      ears: { type: 'floppy_front', width: 0.08, height: 0.09, depth: 0.05 },
      tail: { type: 'pig_curly', length: 0.12, thickness: 0.045, angle: 0.4 }
    }
  },

  // 5. Cừu (Sheep)
  SHEEP: {
    id: 'SHEEP',
    name: 'Cừu Lông Len (Sheep)',
    icon: '🐑',
    archetype: 'quadruped',
    desc: 'Cừu thân bọc lớp khối len xốp nhô ra bồng bềnh, da mặt và 4 chân lộ mịn màng.',
    defaultVariant: 'white_wool',
    variants: {
      white_wool: {
        name: 'Cừu Trắng (White Wool)',
        baseColor: '#f8fafc',
        patternType: 'wool_fluff',
        skinColor: '#fde68a',
        bellyColor: '#f8fafc',
        snoutColor: '#fde68a',
        noseColor: '#fda4af',
        eyeColor: '#0f172a',
        blushColor: '#fda4af'
      },
      black_face: {
        name: 'Cừu Mũi Đen (Blacknose)',
        baseColor: '#f8fafc',
        patternType: 'wool_fluff',
        skinColor: '#1e293b',
        bellyColor: '#f8fafc',
        snoutColor: '#1e293b',
        noseColor: '#020617',
        eyeColor: '#f8fafc',
        blushColor: '#fda4af'
      }
    },
    morph: {
      sizeClass: 'medium',
      scale: 1.5,
      body: { length: 0.52, width: 0.4, height: 0.38 },
      hasWoolLayer: true,
      woolThickness: 0.04,
      head: { sizeX: 0.3, sizeY: 0.28, sizeZ: 0.26 },
      snout: { length: 0.1, width: 0.18, height: 0.12 },
      legs: { height: 0.24, width: 0.09, depth: 0.09, spacingX: 0.26, spacingZ: 0.36 },
      ears: { type: 'side_droop', width: 0.09, height: 0.06, depth: 0.04 },
      tail: { type: 'short_stub', length: 0.08, thickness: 0.05, angle: -0.1 }
    }
  },

  // 6. Ngựa (Horse)
  HORSE: {
    id: 'HORSE',
    name: 'Ngựa Chiến (Horse)',
    icon: '🐴',
    archetype: 'quadruped',
    desc: 'Ngựa vóc dáng cao lớn, chân dài thanh thoát, bờm suôn dài và cử động hoạt bát.',
    defaultVariant: 'chestnut',
    variants: {
      chestnut: {
        name: 'Ngựa Hạt Dẻ (Chestnut)',
        baseColor: '#78350f',
        patternType: 'solid_fur',
        maneColor: '#1e293b',
        bellyColor: '#9a3412',
        snoutColor: '#451a03',
        noseColor: '#0f172a',
        eyeColor: '#0f172a',
        blushColor: '#fda4af'
      },
      black: {
        name: 'Ngựa Ô (Black Steed)',
        baseColor: '#18181b',
        patternType: 'solid_fur',
        maneColor: '#09090b',
        bellyColor: '#18181b',
        snoutColor: '#09090b',
        noseColor: '#020617',
        eyeColor: '#f8fafc',
        blushColor: '#fda4af'
      },
      white: {
        name: 'Ngựa Bạch (White Stallion)',
        baseColor: '#f8fafc',
        patternType: 'solid_fur',
        maneColor: '#cbd5e1',
        bellyColor: '#f8fafc',
        snoutColor: '#f1f5f9',
        noseColor: '#64748b',
        eyeColor: '#0284c7',
        blushColor: '#fda4af'
      }
    },
    morph: {
      sizeClass: 'large',
      scale: 2.1,
      body: { length: 0.58, width: 0.36, height: 0.36 },
      head: { sizeX: 0.3, sizeY: 0.32, sizeZ: 0.32 },
      snout: { length: 0.14, width: 0.2, height: 0.15 },
      hasMane: true,
      mane: { length: 0.3, height: 0.08, thickness: 0.06 },
      legs: { height: 0.44, width: 0.09, depth: 0.09, spacingX: 0.24, spacingZ: 0.4 },
      ears: { type: 'pointy_tall', width: 0.06, height: 0.1, depth: 0.04 },
      tail: { type: 'horse_bushy', length: 0.26, thickness: 0.06, angle: -0.2 }
    }
  },

  // ==========================================
  // II. GIA CẦM 2 CHÂN (AVIAN)
  // ==========================================

  // 7. Gà (Chicken)
  CHICKEN: {
    id: 'CHICKEN',
    name: 'Gà Trống (Chicken)',
    icon: '🐔',
    archetype: 'avian',
    desc: 'Gà lanh lợi, mào đỏ cờ trên đỉnh đầu, tích cằm đỏ rực, cánh khép thân và đuôi lông xòe.',
    defaultVariant: 'rooster',
    variants: {
      rooster: {
        name: 'Gà Trống (Rooster)',
        baseColor: '#b45309',
        patternType: 'feather_layer',
        wingColor: '#991b1b',
        tailColor: '#0f172a',
        combColor: '#dc2626',
        beakColor: '#f59e0b',
        eyeColor: '#0f172a',
        blushColor: '#fb923c'
      },
      hen: {
        name: 'Gà Mái (Hen)',
        baseColor: '#d97706',
        patternType: 'feather_layer',
        wingColor: '#b45309',
        tailColor: '#78350f',
        combColor: '#ef4444',
        beakColor: '#f59e0b',
        eyeColor: '#0f172a',
        blushColor: '#fb923c'
      },
      white: {
        name: 'Gà Trắng (Leghorn)',
        baseColor: '#f8fafc',
        patternType: 'feather_layer',
        wingColor: '#f8fafc',
        tailColor: '#f1f5f9',
        combColor: '#dc2626',
        beakColor: '#f59e0b',
        eyeColor: '#0f172a',
        blushColor: '#fda4af'
      }
    },
    morph: {
      sizeClass: 'small',
      scale: 0.7,
      body: { length: 0.38, width: 0.3, height: 0.32 },
      head: { sizeX: 0.26, sizeY: 0.26, sizeZ: 0.24 },
      beak: { type: 'pointed', length: 0.1, width: 0.08, height: 0.08 },
      comb: { hasComb: true, height: 0.1, width: 0.06, length: 0.16 },
      wattle: { hasWattle: true, height: 0.07, width: 0.05, length: 0.06 },
      wings: { length: 0.22, height: 0.16, thickness: 0.04 },
      legs: { height: 0.14, width: 0.05, depth: 0.05, spacingX: 0.14, footLength: 0.08 },
      tail: { length: 0.14, width: 0.08, height: 0.14, angle: 0.7 }
    }
  },

  // 8. Vịt (Duck)
  DUCK: {
    id: 'DUCK',
    name: 'Vịt Trắng (Duck)',
    icon: '🦆',
    archetype: 'avian',
    desc: 'Vịt thân bè thấp, mỏ dẹt to bè màu cam, chân có màng bơi, dáng đi lắc lư dễ thương.',
    defaultVariant: 'white_pekin',
    variants: {
      white_pekin: {
        name: 'Vịt Xiêm (Pekin)',
        baseColor: '#f8fafc',
        patternType: 'feather_layer',
        wingColor: '#f8fafc',
        tailColor: '#f8fafc',
        beakColor: '#f97316',
        legColor: '#ea580c',
        eyeColor: '#0f172a',
        blushColor: '#fda4af'
      },
      mallard: {
        name: 'Vịt Uyên Ương (Mallard)',
        baseColor: '#78350f',
        patternType: 'mallard',
        headColor: '#047857',
        wingColor: '#0284c7',
        tailColor: '#1e293b',
        beakColor: '#eab308',
        legColor: '#ea580c',
        eyeColor: '#0f172a',
        blushColor: '#fb923c'
      }
    },
    morph: {
      sizeClass: 'small',
      scale: 0.72,
      body: { length: 0.4, width: 0.32, height: 0.28 },
      head: { sizeX: 0.26, sizeY: 0.24, sizeZ: 0.24 },
      beak: { type: 'flat_paddle', length: 0.16, width: 0.16, height: 0.05 },
      comb: { hasComb: false },
      wattle: { hasWattle: false },
      wings: { length: 0.24, height: 0.14, thickness: 0.04 },
      legs: { height: 0.12, width: 0.06, depth: 0.06, spacingX: 0.16, footLength: 0.1, isWebbed: true },
      tail: { length: 0.12, width: 0.1, height: 0.07, angle: 0.2 }
    }
  }
};

/**
 * Trả về danh sách tất cả các loài động vật
 */
export function getAnimalSpeciesList() {
  return Object.values(ANIMAL_PRESETS).map((item) => ({
    id: item.id,
    name: item.name,
    icon: item.icon,
    archetype: item.archetype,
    desc: item.desc,
    defaultVariant: item.defaultVariant,
    variants: Object.keys(item.variants).map((vKey) => ({
      key: vKey,
      name: item.variants[vKey].name
    }))
  }));
}
